import { POEM_POSITION } from '../poems/poem-collection.ts'

/**
 * The page arithmetic of the fullscreen reader, as pure functions.
 *
 * NOTHING HERE TOUCHES REACT, which is the point: paging is the only logic the
 * reader has, and it is the only part of it a test can pin down without a DOM.
 * `use-ebook.ts` holds the state and calls these; they hold no state at all.
 */

/**
 * The page a reader lands on, given where they opened the book.
 *
 * O(1) — one Map probe, the same lookup `poemNeighbours` uses, rather than an
 * `indexOf` scan of the collection.
 *
 * FALLS BACK TO PAGE ONE RATHER THAN FAILING. An unknown slug means the caller
 * asked for a poem that is not published; opening the book at its beginning is the
 * honest answer, and it is what the index's own launcher asks for by passing no
 * slug at all.
 */
export function startIndexFor(slug?: string): number {
  if (slug === undefined) return 0

  return POEM_POSITION.get(slug) ?? 0
}

/**
 * Move `delta` pages from `current`, wrapping at both ends.
 *
 * THE SEQUENCE WRAPS, exactly as `buildNeighbours` does for the prev/next bar,
 * and for the same reason: wrapping means neither control is ever absent, so there
 * is no disabled state to style, no layout branch, and nothing for a reader to
 * discover by finding a button that does nothing. It is also what lets the
 * auto-advance run without a special case for the last page.
 *
 * The double modulo is what makes a backwards step from page one land on the last
 * page: `-1 % 23` is `-1` in JavaScript, not `22`.
 *
 * Returns 0 for an empty book — pure, total, and reachable from a test rather
 * than a branch nothing can execute (see `buildNeighbours` for the precedent).
 */
export function stepPage(current: number, total: number, delta: number): number {
  if (total === 0) return 0

  return (((current + delta) % total) + total) % total
}

/**
 * What the live region says when the page turns.
 *
 * The heading changes too, but a heading inside a dialog is not announced on
 * change — a reader paging with the arrow keys would hear nothing at all. Position
 * first, because "3 de 23" is the part that tells them the page actually turned.
 */
export function pageAnnouncement(index: number, total: number, title: string): string {
  return `Poema ${String(index + 1)} de ${String(total)}: ${title}`
}
